import DescriptionIcon from '@mui/icons-material/Description';
import {
	Autocomplete,
	CircularProgress,
	Grid,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import React, { useEffect } from 'react';
import {
	Controller,
	FieldValues,
	Path,
	UseControllerProps,
	useFormContext,
} from 'react-hook-form';
import { SelectFieldConfiguration } from '../../../api/ConfigApi';
import { usePoliciesForPolicyholder } from '../../../api/PolicyholderApi';
import { useAuthContext } from '../../../context/auth';
import { usePolicyContext } from '../../../context/policy';
import {
	GraceLapseReinstatementResponse,
	Policy2,
} from '../../../types/policy';
import { getPolicyStatus } from '../../../util/util';
import FieldLabel from '../../fields/FieldLabel';
import { FORM_NAME, PAYMENT_SPECIAL_FIELDS } from '../data/constants';

interface PolicyResult {
	product: string;
	locator: string;
	status:
		| 'Lapsed'
		| 'In Grace Period'
		| 'Reinstated'
		| 'Withdrawn'
		| 'Cancelled'
		| 'Issued'
		| 'Accepted'
		| 'Application In Progress';
	isIssued: boolean;
}

const getPolicyFromPoliciesResult = (
	policies: Policy2[],
	graceLapseReinstatementData: GraceLapseReinstatementResponse[],
): PolicyResult[] => {
	return (
		policies?.map((policy) => {
			const product = policy.productName;
			const isIssued = !!policy.issuedTimestamp;
			const policyLocator = policy.locator;
			const policyGraceLapse = graceLapseReinstatementData.find((g) =>
				g ? g.gracePeriod.policyLocator === policy.locator : null,
			);
			const value: PolicyResult = {
				status: getPolicyStatus(policy, policyGraceLapse),
				product,
				isIssued,
				locator: policyLocator,
			};
			return value;
		}) || []
	);
};

type FieldProps = Omit<SelectFieldConfiguration, 'name'>;
interface Props<T> extends FieldProps, UseControllerProps<T> {
	policyholderKey?: string;
	policyKey?: string;
	policiesFilter?: (policy: PolicyResult) => boolean;
}

const PolicyLookupField = <T extends FieldValues>(props: Props<T>) => {
	const {
		name,
		control,
		disabled,
		policyKey,
		policyholderKey,
		policiesFilter,
	} = props;
	const authData = useAuthContext();
	const policyData = usePolicyContext();
	const { watch } = useFormContext();
	const authToken = authData.auth?.authorizationToken;
	const policyholderWatch =
		policyholderKey ||
		`${FORM_NAME.payment}.${PAYMENT_SPECIAL_FIELDS.policyholderLocator}`;
	const policyholderLocator = watch(policyholderWatch);
	const policyWatch =
		policyKey || `${FORM_NAME.payment}.${PAYMENT_SPECIAL_FIELDS.policyLocator}`;
	const prefilledValue = watch(policyWatch);

	const [value, setValue] = React.useState<PolicyResult | null>(null);

	const { data, isLoading: isPolicyholderSearchLoading } =
		usePoliciesForPolicyholder(policyholderLocator, authToken, disabled);

	useEffect(() => {
		if (
			disabled &&
			prefilledValue &&
			policyData?.policy &&
			policyData?.policy?.displayId === prefilledValue
		) {
			const polices = getPolicyFromPoliciesResult(
				[policyData.policy],
				policyData.graceLapse,
			);
			setValue(polices?.[0] || null);
		} else if (prefilledValue && data) {
			const policies = getPolicyFromPoliciesResult(
				data.policies || [],
				data.graceLapseReinstatementData || [],
			);
			const policy = policies.find((p) => p.locator === prefilledValue);
			setValue(policy || null);
		}
	}, [prefilledValue, data, disabled, policyData]);

	const policyOptions = getPolicyFromPoliciesResult(
		data?.policies || [],
		data?.graceLapseReinstatementData || [],
	).filter((p) => (policiesFilter ? policiesFilter(p) : true));

	const isLoading =
		!disabled && isPolicyholderSearchLoading && policyholderLocator !== '';
	return (
		<Controller
			control={control}
			name={name as Path<T>}
			render={({ field: { onChange, onBlur }, fieldState: { error } }) => (
				<FormControl error={!!error} sx={{ width: '100%' }}>
					<FieldLabel title='Policy' htmlFor='policyholder' />
					<Autocomplete
						disabled={disabled}
						loading={isLoading}
						getOptionLabel={(option) =>
							typeof option === 'string' ? option : option.locator
						}
						freeSolo
						filterOptions={(x) => x}
						options={policyOptions}
						autoComplete
						includeInputInList
						value={value}
						isOptionEqualToValue={(option, value) =>
							option.locator === value.locator
						}
						onInputChange={(event, newInputValue) => {
							onChange(newInputValue);
						}}
						onChange={(event: any, newValue: PolicyResult | null) => {
							setValue(newValue);
							onChange(newValue ? newValue.locator : '');
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								fullWidth
								error={!!error}
								helperText={error?.message}
								disabled={disabled}
								onBlur={(e) => {
									onBlur();
								}}
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<React.Fragment>
											{isLoading ? (
												<CircularProgress color='inherit' size={20} />
											) : null}
											{params.InputProps.endAdornment}
										</React.Fragment>
									),
								}}
								sx={{
									'& .MuiInput-input:focus': {
										backgroundColor: 'transparent',
									},
								}}
							/>
						)}
						renderOption={(props, option) => {
							return (
								<li {...props}>
									<Grid container alignItems='center'>
										<Grid item sx={{ display: 'flex', width: 44 }}>
											<DescriptionIcon sx={{ color: 'text.secondary' }} />
										</Grid>
										<Grid
											item
											sx={{
												width: 'calc(100% - 44px)',
												wordWrap: 'break-word',
											}}
										>
											<Stack
												justifyContent='space-between'
												alignItems='center'
												direction='row'
											>
												<Typography variant='body1' color='text.primary'>
													{option.product}
												</Typography>
												<Typography
													variant='body2'
													color='text.secondary'
													sx={{ textAlign: 'right' }}
												>
													{option.status}
												</Typography>
											</Stack>
											<Typography variant='body2' color='text.secondary'>
												{option.locator}
											</Typography>
										</Grid>
									</Grid>
								</li>
							);
						}}
					/>
				</FormControl>
			)}
		/>
	);
};

export default PolicyLookupField;
